"use client";

import React, { useState, useMemo, useCallback } from "react";
import { Stack } from "@mui/material";

import MobileNavbar from "@/components/header/navbar/mobile-navbar";
import MobilePrimaryNavigation from "@/components/header/navigation/mobile/_components/primary-navigation/mobile-primary-navigation";
import MobileSecondaryNavigation from "@/components/header/navigation/mobile/_components/secondary-navigation/mobile-secondary-navigation";
import MobileTertiaryNavigation from "@/components/header/navigation/mobile/_components/tertiary-navigation/mobile-tertiary-navigation";

import { NavigationType } from "@/components/header/common/header-enums";
import { Company } from "@/types/contentful/company";
import { HeaderNavProps } from "@/components/header/common/types";
import { Locale } from "@/i18n/routing";
import useScrollLock from "@/hooks/scroll-lock/use-scroll-lock";

type MobileStandardHeaderProps = {
  locale: Locale;
  headerNavProps: HeaderNavProps[];
  companyList: Company[];
};

type NavigationState = {
  type: NavigationType;
  primaryLabel: string;
  secondaryItems: HeaderNavProps[];
  tertiaryLabel: string;
  tertiaryItems: HeaderNavProps[];
  tertiaryLearnMoreUrl: string;
  tertiaryViewAllDestinationsUrl: string;
};

export default function MobileComponentManager(props: MobileStandardHeaderProps) {
  const { locale, companyList, headerNavProps } = props;

  const defaultSecondaryNavItems = useMemo(() =>
    headerNavProps[0]?.fields.subNavList ?? [],
    [headerNavProps]
  );

  const defaultTertiaryHeaderLabel = useMemo(() =>
    defaultSecondaryNavItems[0]?.fields.title ?? "",
    [defaultSecondaryNavItems]
  );

  const defaultTertiaryNavItems = useMemo(() =>
    defaultSecondaryNavItems[0]?.fields.subNavList ?? [],
    [defaultSecondaryNavItems]
  );

  const [navigationState, setNavigationState] = useState<NavigationState>({
    type: NavigationType.base,
    primaryLabel: "",
    secondaryItems: defaultSecondaryNavItems,
    tertiaryLabel: defaultTertiaryHeaderLabel,
    tertiaryItems: defaultTertiaryNavItems,
    tertiaryLearnMoreUrl: "",
    tertiaryViewAllDestinationsUrl: ""
  });

  const updateNavigationState = useCallback((updates: Partial<NavigationState>) => {
    setNavigationState(prev => ({ ...prev, ...updates }));
  }, []);

  useScrollLock(navigationState.type !== "base");

  const renderNavigation = useCallback(() => {
    const {
      type,
      primaryLabel,
      secondaryItems,
      tertiaryLabel,
      tertiaryItems,
      tertiaryLearnMoreUrl
    } = navigationState;

    switch (type) {
      case NavigationType.base:
        return (
          <MobileNavbar
            currentCompany={companyList[0]}
            setActiveNavigation={(newType) => updateNavigationState({ type: newType })}
            locale={locale}
          />
        );
      case NavigationType.primary:
        return (
          <MobilePrimaryNavigation
            locale={locale}
            currentCompany={companyList[0]}
            headerNavProps={headerNavProps}
            setSecondaryNavItems={(items) => updateNavigationState({ secondaryItems: items })}
            setCurrentPrimaryNavLabel={(label) => updateNavigationState({ primaryLabel: label })}
            setActiveNavigation={(newType) => updateNavigationState({ type: newType })}
          />
        );
      case NavigationType.secondary:
        return (
          <MobileSecondaryNavigation
            secondaryNavItems={secondaryItems}
            currentPrimaryNavLabel={primaryLabel}
            setActiveNavigation={(newType) => updateNavigationState({ type: newType })}
            setTertiaryHeaderLabel={(label) => updateNavigationState({ tertiaryLabel: label })}
            setTertiaryNavItems={(items) => updateNavigationState({ tertiaryItems: items })}
            setTertiaryHeaderLearnMoreUrl={(url) => updateNavigationState({ tertiaryLearnMoreUrl: url })}
            setTertiaryHeaderViewAllDestinationsUrl={(url) => updateNavigationState({ tertiaryViewAllDestinationsUrl: url })}
          />
        );
      case NavigationType.tertiary:
        return (
          <MobileTertiaryNavigation
            setActiveNavigation={(newType) => updateNavigationState({ type: newType })}
            tertiaryHeaderLabel={tertiaryLabel}
            tertiaryHeaderLearnMoreUrl={tertiaryLearnMoreUrl}
            tertiaryNavItems={tertiaryItems}
          />
        );
      default:
        return null;
    }
  }, [navigationState, companyList, locale, headerNavProps, updateNavigationState]);

  return (
    <Stack sx={{ overflowX: "hidden", width: "100%" }} id="mobile-component-manager">
      {renderNavigation()}
    </Stack>
  );
}